import { Component, effect, inject, OnInit, signal, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject, catchError, finalize, map, of, takeUntil, throwError } from 'rxjs';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { MessageService, SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { TranslateService } from '@ngx-translate/core';
import { createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues } from '@card-software/library';
import { AuthService, debouncedServerSearch } from '@card-software/nexus-utilities';
import { Business, BusinessEntityType } from '../../../models/finance/business';
import { Color } from '../../../models/lab-dip/color';
import { LabDipRequest } from '../../../models/lab-dip/lab-dip-request';
import { LabDipRequestSearch } from '../../../models/commands/lab-dip-request-search';
import { LapDipRequestRoles } from '../../../models/authorization/enum/user-group.enum';
import { UserMaster } from '../../../models/user-master/user-master';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { LabDipRequestProxyService } from '../../../proxies/item/lab-dip-request.proxy.service';
import { UserMasterProxyService } from '../../../proxies/user-master/user-master.proxy.service';
import { LocaleHelperService } from '../../../services/localehelperservice.service';

@Component({
  selector: 'app-lab-dips',
  templateUrl: './lab-dip-requests.component.html',
  styleUrl: './lab-dip-requests.component.scss',
})
export class LabDipRequestsComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private translateService = inject(TranslateService);
  public messageService = inject(MessageService);
  private businessProxyService = inject(BusinessProxyService);
  private labDipRequestProxyService = inject(LabDipRequestProxyService);
  private userMasterProxyService = inject(UserMasterProxyService);
  public localeHelperService = inject(LocaleHelperService);

  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'lab-dip-requests-filters', config: this.filterConfig });
  showFilters = signal(false);

  // Pagination
  currentPage = signal(1);
  pageSize = signal(25);
  totalRecords = signal(0);

  // Sorting
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  // Data
  listLabDipRequests = signal<LabDipRequest[]>([]);

  // Autocomplete suggestions (populated on demand)
  filteredBrands = signal<Business[]>([]);
  filteredCustomers = signal<Business[]>([]);
  filteredUsers = signal<UserMaster[]>([]);

  // Users are lazy-loaded on first autocomplete interaction
  private cachedUsers: UserMaster[] = [];
  private usersLoaded = false;

  private brandSearch$ = new Subject<string>();
  private customerSearch$ = new Subject<string>();
  private destroy$ = new Subject<void>();
  private filtersPanelInitialized = false;
  private lastSearchCriteria: LabDipRequestSearch = { username: '', client: '', brand: '', labDipIdPattern: '' };

  customersMap: Record<string, Business> = {};
  brandsMap: Record<string, Business> = {};
  usersMap: Record<string, UserMaster> = {};

  translateFilter = effect(
    () => {
      if (this.translateService.currentLang) {
        this.tryInitializeFilterConfig();
      }
    },
    { allowSignalWrites: true },
  );

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
      if (!this.filtersPanelInitialized) {
        this.filtersPanelInitialized = true;
        this.showFilters.set(this.filterState.hasActive());
      }
      this.searchWithFilters(filters);
    });
  }

  getRowData(rowData: unknown): LabDipRequest {
    return rowData as LabDipRequest;
  }

  canEdit() {
    if (AuthService.hasRole(LapDipRequestRoles.LabDipRequestAdmin) || AuthService.hasRole(LapDipRequestRoles.LabDipRequestEditor)) {
      return true;
    }
    if (AuthService.hasRole(LapDipRequestRoles.LabDipRequestViewer)) {
      return false;
    }
    return false;
  }

  ngOnInit() {
    // These filters are configs for the dynamic-filters panel, so there is no <p-autoComplete> here to
    // hang ServerSearchDirective on: the shared operator is wired by hand. It replaces the local
    // debounce + switchMap, whose catchError-less switchMap let one failed request kill the stream for
    // the rest of the page's life.
    this.brandSearch$
      .pipe(
        debouncedServerSearch((query) => this.searchBusinesses(BusinessEntityType.Brand, query)),
        takeUntil(this.destroy$),
      )
      .subscribe((brands) => this.filteredBrands.set(brands));

    this.customerSearch$
      .pipe(
        debouncedServerSearch((query) => this.searchBusinesses(BusinessEntityType.Client, query)),
        takeUntil(this.destroy$),
      )
      .subscribe((customers) => this.filteredCustomers.set(customers));
  }

  private searchBusinesses(businessType: BusinessEntityType, query: string): Observable<Business[]> {
    return this.businessProxyService
      .search({ name: query, businessTypes: [businessType] }, 1, 25)
      .pipe(map((response) => (response.data?.items || []).map((b) => ({ ...b, fieldToDisplay: `(${b.code}) ${b.name}` }))));
  }

  ngOnDestroy(): void {
    this.brandSearch$.complete();
    this.customerSearch$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'autocomplete',
          formControlName: 'username',
          label: translate('LabDipRequests_Username', 'Username'),
          placeholder: translate('Select_username', 'Select username'),
          autocompleteConfig: {
            suggestions: this.filteredUsers(),
            optionLabel: 'fieldToDisplay',
            optionValue: 'username',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterUsers(event),
            resolveById: (username: string) => this.resolveUser(username),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'client',
          label: translate('LabDipRequests_Client', 'Client'),
          placeholder: translate('Select_client', 'Select client'),
          autocompleteConfig: {
            suggestions: this.filteredCustomers(),
            optionLabel: 'fieldToDisplay',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterCustomers(event),
            resolveById: (id: string) => this.resolveBusiness(id),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'brand',
          label: translate('LabDipRequests_Brand', 'Brand'),
          placeholder: translate('Select_brand', 'Select brand'),
          autocompleteConfig: {
            suggestions: this.filteredBrands(),
            optionLabel: 'fieldToDisplay',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterBrands(event),
            resolveById: (id: string) => this.resolveBusiness(id),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: true,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  onSearch(searchCriteria: LabDipRequestSearch): void {
    this.lastSearchCriteria = searchCriteria;
    this.startLoading();

    this.labDipRequestProxyService
      .search(searchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.listLabDipRequests.set(response?.data?.items || []);
          this.totalRecords.set(response?.data?.totalCount || 0);
        },
        error: () => {
          this.listLabDipRequests.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page || 0) + 1);
    this.pageSize.set(event.rows || this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    this.sortField.set(event.field);
    this.sortOrder.set(event.order ?? -1);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  filterBrands(event: AutoCompleteCompleteEvent): void {
    this.brandSearch$.next(event.query);
  }

  filterCustomers(event: AutoCompleteCompleteEvent): void {
    this.customerSearch$.next(event.query);
  }

  filterUsers(event: AutoCompleteCompleteEvent): void {
    const applyFilter = () => {
      const query = event.query.toLowerCase();
      this.filteredUsers.set(
        this.cachedUsers.filter((user) => {
          const profile = user.profile;
          return (
            user.username.toLowerCase().includes(query) ||
            profile?.firstName?.toLowerCase().includes(query) ||
            profile?.lastName?.toLowerCase().includes(query) ||
            user.fieldToDisplay?.toLowerCase().includes(query)
          );
        }),
      );
    };

    if (this.usersLoaded) {
      applyFilter();
    } else {
      this.userMasterProxyService.getUsers().subscribe({
        next: (response) => {
          this.cachedUsers = (response.data || []).map((user) => this.toUserOption(user));
          this.usersLoaded = true;
          applyFilter();
        },
      });
    }
  }

  getColorsString(colors?: Color[] | null): string {
    if (!colors || colors.length === 0) return '';
    return colors.map((c) => c.name).join(', ');
  }

  redirectToDetails(id?: string): void {
    const queryParams = id ? { id: id } : {};
    this.router.navigate(['/lab-dip-request-details'], { queryParams });
  }

  private toUserOption(user: UserMaster): UserMaster {
    const profile = user.profile;
    const nameParts = [profile?.firstName, profile?.middleName, profile?.lastName, profile?.secondLastName].filter(
      (part) => part && part.trim() !== '',
    );
    const fullName = nameParts.length > 0 ? nameParts.join(' ') : user.username;
    return { ...user, fieldToDisplay: fullName };
  }

  private resolveUser(username: string): Observable<UserMaster | null> {
    return this.userMasterProxyService.getUserByUsername(username).pipe(
      map((response) => (response.data ? this.toUserOption(response.data) : null)),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  private resolveBusiness(id: string): Observable<Business | null> {
    return this.businessProxyService.searchBatchIds([id]).pipe(
      map((response) => {
        const business = response.data?.find((item) => item.id === id);
        return business ? { ...business, fieldToDisplay: `(${business.code}) ${business.name}` } : null;
      }),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const searchCriteria: LabDipRequestSearch = {
      username: (filters['username'] as string)?.trim() || '',
      client: (filters['client'] as string) || '',
      brand: (filters['brand'] as string) || '',
      labDipIdPattern: (filters['labDipIdPattern'] as string)?.trim() || '',
    };

    this.currentPage.set(1);
    this.onSearch(searchCriteria);
  }
}
